'use client';

import { useEffect, useState } from 'react';
import Link from '@eliadmin/core/src/components/link';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import ProductVariantColor from '@eliadmin/core/src/components/product/detail/variants/product-variant-color';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { AttributeType } from '@eliadmin/core/src/stores/category-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { AddToCartButton } from '../add-to-cart-button';
import { isSellable } from '@eliadmin/core/src/utils/product-utils';

export const ProductBar = ({ product }: { product: ProductDetailData }) => {
  const attributes = useVariantsStore(state => state.product.attributes) || [];
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const variants = useVariantsStore(state => state.product.variants);

  const [isSticky, setIsSticky] = useState(false);

  const calculateHeight = () => {
    const width = window.innerWidth;
    if (width >= 1920) {
      return window.innerHeight * 0.82;
    } else if (width >= 1536) {
      return window.innerHeight * 0.75;
    } else if (width >= 1280) {
      return window.innerHeight * 0.71;
    } else if (width >= 1024) {
      return window.innerHeight * 0.7;
    } else if (width >= 768) {
      return window.innerHeight * 0.72;
    } else {
      return window.innerHeight * 0.5;
    }
  };

  useEffect(() => {
    const handleScrollEffect = () => {
      const scrollPosition = window.scrollY;
      const viewportHeight = calculateHeight();

      setIsSticky(scrollPosition > viewportHeight);
    };

    window.addEventListener('scroll', handleScrollEffect);
    window.addEventListener('resize', handleScrollEffect);

    return () => {
      window.removeEventListener('scroll', handleScrollEffect);
      window.removeEventListener('resize', handleScrollEffect);
    };
  }, []);

  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const setSelectedQuantity = useVariantsStore(state => state.setSelectedQuantity);

  const sellable = isSellable(selectedVariant ? selectedVariant : product, selectedQuantity);

  const disabled =
    (product.quantity < 1 && !sellable) || ((variants?.length || 0) > 0 && !selectedVariant);

  useEffect(() => {
    const tmp = selectedVariant ? selectedVariant : product;
    if (tmp.quantity === 0 && tmp.availability.sellable) {
      setSelectedQuantity(1);
    }
  }, [product, selectedVariant, setSelectedQuantity]);

  const handleScrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 120; // Adjust for sticky header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // Update hash after scroll starts
      setTimeout(() => {
        window.location.hash = sectionId;
      }, 100);
    }
  };

  return (
    <div
      className={cn(
        'custom-section',
        'sticky top-[83px] z-[48] bg-white py-6 sm:top-[94px] md:top-[95px] lg:top-[111px]',
        isSticky && 'ea-product-bar__shadow border-t border-t-[#D0D0D0]',
      )}
    >
      <div className={cn('custom-container', 'hidden items-center justify-between gap-8 md:flex')}>
        <div className="flex w-full items-center justify-start gap-8">
          <div className="flex flex-col items-start justify-start gap-1">
            <h1
              className={cn(
                'font-heading uppercase',
                'line-clamp-1 overflow-ellipsis text-3xl font-medium text-foreground xl:text-4xl',
              )}
            >
              {product.name}
            </h1>
            <p className="text-base text-[#616161]">
              Katalógové číslo:&nbsp;{selectedVariant?.code ?? product.code}
            </p>
          </div>

          {attributes.some(
            attribute =>
              attribute.type === AttributeType.COLOR || attribute.type === AttributeType.MULTICOLOR,
          ) &&
            attributes
              .filter(
                attribute =>
                  attribute.type === AttributeType.COLOR ||
                  attribute.type === AttributeType.MULTICOLOR,
              )
              .map((attribute, i) => (
                <ProductVariantColor key={`attribute-${i}`} attribute={attribute} />
              ))}
        </div>

        <div className="flex items-center justify-end gap-8">
          <Button
            asChild
            variant="ghost"
            className="hidden whitespace-nowrap !p-0 text-primary hover:bg-transparent hover:text-primary lg:flex"
          >
            <Link
              prefetch={false}
              href="#popis-produktu"
              onClick={e => handleScrollToSection(e, 'popis-produktu')}
            >
              Popis produktu <MdKeyboardArrowDown size={20} />
            </Link>
          </Button>

          <Button
            asChild
            variant="ghost"
            className="hidden whitespace-nowrap !p-0 text-primary hover:bg-transparent hover:text-primary lg:flex"
          >
            <Link
              prefetch={false}
              href="#technicke-parametre"
              onClick={e => handleScrollToSection(e, 'technicke-parametre')}
            >
              Technické parametre <MdKeyboardArrowDown size={20} />
            </Link>
          </Button>

          <AddToCartButton
            quantity={selectedQuantity}
            disabled={disabled}
            product={product}
            customButton
            sellable={sellable}
          />
        </div>
      </div>

      <div className="flex w-full flex-col items-start justify-start gap-4 md:hidden">
        <div className="flex flex-col items-start justify-start gap-1">
          <h1
            className={cn(
              'font-heading uppercase',
              'text-2xl font-medium text-foreground sm:text-3xl xl:text-4xl',
            )}
          >
            {product.name}
          </h1>
          <p className="text-xs text-[#616161] sm:text-sm md:text-base">
            Katalógové číslo:&nbsp;&nbsp;{selectedVariant?.code ?? product.code}
          </p>
        </div>

        <div className="flex w-full items-center justify-between">
          {attributes.some(
            attribute =>
              attribute.type === AttributeType.COLOR || attribute.type === AttributeType.MULTICOLOR,
          ) &&
            attributes
              .filter(
                attribute =>
                  attribute.type === AttributeType.COLOR ||
                  attribute.type === AttributeType.MULTICOLOR,
              )
              .map((attribute, i) => (
                <ProductVariantColor key={`attribute-${i}`} attribute={attribute} />
              ))}

          <AddToCartButton
            quantity={selectedQuantity}
            disabled={disabled}
            product={product}
            customButton
            sellable={sellable}
          />
        </div>
      </div>
    </div>
  );
};
